import api from "../../utils/axios";
import { useState, useCallback } from "react";

interface ArticuloItem {
  Id: number;
  IdCategoria: number;
  IdEmbalaje: number;
  IdImpuesto: number;
  Nombre: string;
  Descripcion?: string;
  FechaAlta: string;
  EAN: string;
  UnidadesPorEmbalaje: number;
  Referencia?: string;
  PrecioCompra: number;
  PrecioVenta: number;
  Descuento: number;
  Activo: boolean;
  Eliminado: boolean;
  categoria?: {
    Id: number;
    Nombre: string;
  };
  embalaje?: {
    Id: number;
    Nombre: string;
  };
  impuesto?: {
    Id: number;
    Nombre: string;
  };
}

interface ArticuloDto {
  IdCategoria: number;
  IdEmbalaje: number;
  IdImpuesto: number;
  Nombre: string;
  Descripcion?: string;
  FechaAlta: string;
  EAN: string;
  UnidadesPorEmbalaje: number;
  Referencia?: string;
  PrecioCompra: number;
  PrecioVenta: number;
  Descuento: number;
  Activo: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
}

//export interface ArticuloDto extends Omit<ArticuloItem, 'Id' | 'categoria' | 'embalaje' | 'impuesto' | 'Eliminado'> {}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
}

export interface ArticuloSearchResult {
  Id: number;
  EAN: string;
  Nombre: string;
  Descripcion: string;
  PrecioCompra: number;
  IdImpuesto: number;
  articuloNombre: string;
  FechaCompra?: string;  // Agregar este campo
}

interface ArticulosApiHook {
  isLoading: boolean;
  getAll: (
    page?: number,
    limit?: number
  ) => Promise<PaginatedResult<ArticuloItem>>;
  create: (data: ArticuloDto) => Promise<ArticuloItem>;
  update: (id: number, data: ArticuloDto) => Promise<ArticuloItem>;
  remove: (id: number) => Promise<boolean>;
}

const useArticulosApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = "/almacen/articulos";

  // Utility function for API calls
  const apiCall = async <T>(
    endpoint: string,
    method: "GET" | "POST" = "POST",
    data?: any,
    config: any = {}
  ): Promise<T> => {
    setIsLoading(true);
    try {
      const response =
        method === "POST"
          ? await api.post<T>(`${baseUrl}${endpoint}`, data, config)
          : await api.get<T>(`${baseUrl}${endpoint}`, config);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAll = useCallback(async (page = 1, limit = 10, search?: string) => {
    setIsLoading(true);
    try {
      const response = await api.post<PaginatedResult<ArticuloItem>>(
        `${baseUrl}/findAll`,
        { page, limit, ...(search ? { search } : {}) }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const findOne = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<ArticuloItem>(`${baseUrl}/findOne/${id}`);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: ArticuloDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: number, data: ArticuloDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await api.post(`${baseUrl}/softDelete/${id}`);
      return true;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      const response = await api.get(`${baseUrl}/download-template`, {
        responseType: "blob", // Importante para manejar archivos binarios
      });

      // Crear URL del blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Crear link temporal y simular click
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_articulos.xlsx");
      document.body.appendChild(link);
      link.click();

      // Limpieza
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error descargando plantilla:", error);
      throw error;
    }
  }, []);

  const importExcel = useCallback(async (formData: FormData) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/import-excel`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);
  const searchArticulos = useCallback(async (searchTerm: string) => {
    setIsLoading(true);
    try {
      const response = await api.post<ArticuloSearchResult[]>(`${baseUrl}/search`, { searchTerm });
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    getAll,
    create,
    update,
    remove,
    downloadTemplate,
    importExcel,
    searchArticulos,
    findOne  
  };
};

// services/api/inventariosApi.ts
interface InventarioItem {
  Id: number;
  IdArticulo: number;
  IdSucursal: number;
  stock: number;
  StockMinimo: number;
  articulo?: {
    Id: number;
    Nombre: string;
    Descripcion?: string;
  };
  sucursal?: {
    Id: number;
    Nombre: string;
  };
}

interface InventarioDto {
  IdArticulo: number;
  IdSucursal: number;
  stock: number;
  StockMinimo: number;
}

const useInventariosApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = "/almacen/inventarios";

  const getAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<InventarioItem[]>(`${baseUrl}/findAll`);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: InventarioDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: number, data: InventarioDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await api.post(`${baseUrl}/remove/${id}`);
      return true;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateStock = useCallback(async (id: number, newStock: number) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/updateStock/${id}`, {
        stock: newStock,
      });
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

 

  return {
    isLoading,
    getAll,
    create,
    update,
    remove,
    updateStock,    
  };
};

export { useArticulosApi, useInventariosApi };
export type { ArticuloItem, ArticuloDto, InventarioItem, InventarioDto };
